import { useEffect, useState } from "react";
import { nanoid } from "nanoid";
import validator from "validator";

import { storage } from "../utils/firebase";
import useLocalStorage from "./useLocalStorage";

const {
  getDownloadURL,
  getMetadata,
  ref,
  uploadBytesResumable,
  deleteObject,
  listAll,
  firebaseStorage,
} = storage;

const useFileupload = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const {
    storageKeys,
    getSessionStorageItem,
    getSessionStorageJson,
    setSessionStorageItem,
    setSessionStorageJson,
    clearSessionStorage,
  } = useLocalStorage();

  // Simulate upload progress
  useEffect(() => {
    let intervalId: any = undefined;

    if (loading) {
      intervalId = setInterval(() => {
        if (progress < 100) {
          setProgress(progress + 1);
        }
      }, 200);
    }

    return () => clearInterval(intervalId);
  }, [loading, progress]);

  const generateRandomId = () =>
    validator.blacklist(nanoid(), "\\_\\-").substring(0, 8);

  const getSavedUploadedFiles = () =>
    getSessionStorageJson(storageKeys.medataFiles) ?? [];

  const saveUploadedFile = (file: any) => {
    const savedFiles = getSavedUploadedFiles();

    savedFiles.push(file);

    setSessionStorageJson(storageKeys.medataFiles, savedFiles);
  };

  const deleteUploadedFile = (file: any) => {
    const savedFiles = getSavedUploadedFiles().filter(
      (savedFile: any) => savedFile.name !== file.name
    );

    setSessionStorageJson(storageKeys.medataFiles, savedFiles);
  };

  const getFilePath = (file?: any) => {
    let randomId = getSessionStorageItem(storageKeys.randomId);

    // Create a tracking number for current session (unique per tab)
    if (!randomId) {
      clearSessionStorage()
      randomId = generateRandomId();
      setSessionStorageItem(storageKeys.randomId, randomId);
    }

    return file ? `${randomId}/${file.name}` : randomId;
  };

  const fbFileUpload = (file: any, pdfPages: number) =>
    new Promise((resolve) => {
      const savedFiles = getSavedUploadedFiles().filter(
        (savedFile: any) => savedFile.name === file.name
      );
      const isFileUploaded = savedFiles.length === 1;

      if (isFileUploaded) {
        resolve(savedFiles[0]);
      } else {
        setProgress(0);
        setLoading(true);

        const storageRef = ref(firebaseStorage, getFilePath(file));
        const uploadTask = uploadBytesResumable(storageRef, file);

        const onProgress = (snapshot: any) => {
          const loadingProgress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setProgress(loadingProgress);
        };

        const onError = (error: any) => {
          console.log("Upload error!", error);
        };

        const onComplete = () => {
          getMetadata(storageRef).then((fileMetadata) => {
            getDownloadURL(storageRef).then((downloadUrl) => {
              // Some slack for animation
              setTimeout(() => {
                setLoading(false);
              }, 500);

              const result = {
                ...fileMetadata,
                downloadUrl,
                pdfPages,
              };

              saveUploadedFile(result);
              resolve(result);
            });
          });
        };

        uploadTask.on("state_changed", onProgress, onError, onComplete);
      }
    });

  const fbFileDelete = async (file: any) => {
    deleteUploadedFile(file);
    const result = await deleteObject(ref(firebaseStorage, getFilePath(file)));

    return result;
  };

  const fbFileList = async () => {
    const listRef = ref(firebaseStorage, getFilePath());

    const response = await listAll(listRef);
    const result = await Promise.all(
      response.items.map((item) => getMetadata(item))
    );

    return result;
  };

  const fbGetUploadedFiles = async () => {
    return getSavedUploadedFiles();
  };

  return {
    fbFileUpload,
    fbFileDelete,
    fbFileList,
    fbGetUploadedFiles,
    loading,
    progress,
  };
};

export default useFileupload;
